import axios from '../axiosConfig';

/**
 * GPS Device Management Service
 * Handles all API calls related to GPS device management
 * Includes caching for device lists and statistics
 */
class GPSDeviceService {
  constructor() {
    this.cache = {};
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes cache
  }

  /**
   * Check if cache is valid
   */
  isCacheValid(cacheKey) {
    const cache = this.cache[cacheKey];
    if (!cache || !cache.data || !cache.timestamp) {
      return false;
    }
    return Date.now() - cache.timestamp < this.cacheTimeout;
  }

  /**
   * Get all GPS devices for an entity
   * @param {number} entityId - Entity ID
   * @param {boolean} forceRefresh - Skip cache
   * @returns {Promise<Array>} Array of GPS devices
   */
  async getAllDevices(entityId, forceRefresh = false) {
    const cacheKey = `devices_${entityId}`;

    if (!forceRefresh && this.isCacheValid(cacheKey)) {
      console.log('Using cached GPS devices data');
      return this.cache[cacheKey].data;
    }

    try {
      console.log('Fetching GPS devices from API', { entityId });
      const response = await axios.get(`/api/gps-devices/${entityId}/devices`);

      if (response.data.success) {
        this.cache[cacheKey] = {
          data: response.data.data,
          timestamp: Date.now()
        };

        console.log('GPS devices fetched successfully', {
          entityId,
          count: response.data.data.length
        });
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to fetch GPS devices');
      }
    } catch (error) {
      console.error('Error fetching GPS devices', {
        error: error.message,
        entityId,
        component: 'GPSDeviceService'
      });

      // Return cached data if available
      if (this.cache[cacheKey]?.data) {
        console.log('Using stale cached GPS devices data due to API error');
        return this.cache[cacheKey].data;
      }

      throw error;
    }
  }

  /**
   * Get single GPS device by ID
   * @param {number} entityId - Entity ID
   * @param {number} deviceId - Device ID
   * @returns {Promise<Object>} GPS device object
   */
  async getDevice(entityId, deviceId) {
    try {
      console.log('Fetching GPS device', { entityId, deviceId });
      const response = await axios.get(`/api/gps-devices/${entityId}/devices/${deviceId}`);

      if (response.data.success) {
        console.log('GPS device fetched successfully', { deviceId });
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to fetch GPS device');
      }
    } catch (error) {
      console.error('Error fetching GPS device', {
        error: error.message,
        entityId,
        deviceId,
        component: 'GPSDeviceService'
      });
      throw error;
    }
  }

  /**
   * Create new GPS device
   * @param {number} entityId - Entity ID
   * @param {Object} deviceData - Device data
   * @returns {Promise<Object>} Created device with API key
   */
  async createDevice(entityId, deviceData) {
    try {
      console.log('Creating GPS device', { entityId, deviceData });
      const response = await axios.post(
        `/api/gps-devices/${entityId}/devices`,
        deviceData
      );

      if (response.data.success) {
        // Invalidate cache
        this.clearCache(entityId);

        console.log('GPS device created successfully', {
          deviceId: response.data.data.id,
          deviceName: response.data.data.device_name
        });
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to create GPS device');
      }
    } catch (error) {
      console.error('Error creating GPS device', {
        error: error.message,
        entityId,
        component: 'GPSDeviceService'
      });
      throw error;
    }
  }

  /**
   * Update GPS device
   * @param {number} entityId - Entity ID
   * @param {number} deviceId - Device ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated device
   */
  async updateDevice(entityId, deviceId, updates) {
    try {
      console.log('Updating GPS device', { entityId, deviceId, updates });
      const response = await axios.put(
        `/api/gps-devices/${entityId}/devices/${deviceId}`,
        updates
      );

      if (response.data.success) {
        // Invalidate cache
        this.clearCache(entityId);

        console.log('GPS device updated successfully', { deviceId });
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to update GPS device');
      }
    } catch (error) {
      console.error('Error updating GPS device', {
        error: error.message,
        entityId,
        deviceId,
        component: 'GPSDeviceService'
      });
      throw error;
    }
  }

  /**
   * Rotate a GPS device API key and return the one-time plaintext key.
   * The returned key must not be cached or logged because the backend only
   * reveals it once for device reconfiguration.
   * @param {number} entityId - Entity ID
   * @param {number} deviceId - Device ID
   * @returns {Promise<Object>} Updated device with one-time api_key
   */
  async rotateApiKey(entityId, deviceId) {
    try {
      console.log('Rotating GPS device API key', { entityId, deviceId });
      const response = await axios.post(
        `/api/gps-devices/${entityId}/devices/${deviceId}/rotate-api-key`
      );

      if (response.data.success) {
        this.clearCache(entityId);

        console.log('GPS device API key rotated successfully', {
          entityId,
          deviceId,
          apiKeyConfigured: Boolean(response.data.data?.api_key_configured)
        });
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to rotate GPS device API key');
      }
    } catch (error) {
      console.error('Error rotating GPS device API key', {
        error: error.message,
        entityId,
        deviceId,
        component: 'GPSDeviceService'
      });
      throw error;
    }
  }

  /**
   * Delete GPS device
   * @param {number} entityId - Entity ID
   * @param {number} deviceId - Device ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteDevice(entityId, deviceId) {
    try {
      console.log('Deleting GPS device', { entityId, deviceId });
      const response = await axios.delete(
        `/api/gps-devices/${entityId}/devices/${deviceId}`
      );

      if (response.data.success) {
        // Invalidate cache
        this.clearCache(entityId);

        console.log('GPS device deleted successfully', { deviceId });
        return true;
      } else {
        throw new Error(response.data.error || 'Failed to delete GPS device');
      }
    } catch (error) {
      console.error('Error deleting GPS device', {
        error: error.message,
        entityId,
        deviceId,
        component: 'GPSDeviceService'
      });
      throw error;
    }
  }

  /**
   * Assign GPS device to vehicle
   * @param {number} entityId - Entity ID
   * @param {number} deviceId - Device ID
   * @param {number} vehicleId - Vehicle ID
   * @param {boolean} setAsDefault - Set as default device
   * @returns {Promise<Object>} Updated device
   */
  async assignToVehicle(entityId, deviceId, vehicleId, setAsDefault = false) {
    try {
      console.log('Assigning GPS device to vehicle', {
        entityId,
        deviceId,
        vehicleId,
        setAsDefault
      });
      const response = await axios.put(
        `/api/gps-devices/${entityId}/devices/${deviceId}/assign`,
        { vehicleId, setAsDefault }
      );

      if (response.data.success) {
        // Invalidate cache
        this.clearCache(entityId);

        console.log('GPS device assigned successfully', { deviceId, vehicleId });
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to assign GPS device');
      }
    } catch (error) {
      console.error('Error assigning GPS device', {
        error: error.message,
        entityId,
        deviceId,
        vehicleId,
        component: 'GPSDeviceService'
      });
      throw error;
    }
  }

  /**
   * Unassign GPS device from vehicle
   * @param {number} entityId - Entity ID
   * @param {number} deviceId - Device ID
   * @returns {Promise<Object>} Updated device
   */
  async unassignFromVehicle(entityId, deviceId) {
    try {
      console.log('Unassigning GPS device from vehicle', { entityId, deviceId });
      const response = await axios.put(
        `/api/gps-devices/${entityId}/devices/${deviceId}/unassign`
      );

      if (response.data.success) {
        // Invalidate cache
        this.clearCache(entityId);

        console.log('GPS device unassigned successfully', { deviceId });
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to unassign GPS device');
      }
    } catch (error) {
      console.error('Error unassigning GPS device', {
        error: error.message,
        entityId,
        deviceId,
        component: 'GPSDeviceService'
      });
      throw error;
    }
  }

  /**
   * Set device as default for its vehicle
   * @param {number} entityId - Entity ID
   * @param {number} deviceId - Device ID
   * @returns {Promise<Object>} Updated device
   */
  async setAsDefault(entityId, deviceId) {
    try {
      console.log('Setting GPS device as default', { entityId, deviceId });
      const response = await axios.put(
        `/api/gps-devices/${entityId}/devices/${deviceId}/set-default`
      );

      if (response.data.success) {
        // Invalidate cache
        this.clearCache(entityId);

        console.log('GPS device set as default successfully', { deviceId });
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to set device as default');
      }
    } catch (error) {
      console.error('Error setting GPS device as default', {
        error: error.message,
        entityId,
        deviceId,
        component: 'GPSDeviceService'
      });
      throw error;
    }
  }

  /**
   * Get GPS devices assigned to a vehicle
   * @param {number} entityId - Entity ID
   * @param {number} vehicleId - Vehicle ID
   * @returns {Promise<Array>} Array of devices
   */
  async getDevicesByVehicle(entityId, vehicleId) {
    try {
      console.log('Fetching GPS devices for vehicle', { entityId, vehicleId });
      const response = await axios.get(
        `/api/gps-devices/${entityId}/devices/vehicle/${vehicleId}`
      );

      if (response.data.success) {
        console.log('GPS devices for vehicle fetched successfully', {
          vehicleId,
          count: response.data.data.length
        });
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to fetch devices for vehicle');
      }
    } catch (error) {
      console.error('Error fetching GPS devices for vehicle', {
        error: error.message,
        entityId,
        vehicleId,
        component: 'GPSDeviceService'
      });
      throw error;
    }
  }

  /**
   * Get available GPS devices for assignment (unassigned devices)
   * Optionally includes devices assigned to a specific vehicle (for edit mode)
   * @param {number} entityId - Entity ID
   * @param {number} vehicleId - Optional vehicle ID (includes devices assigned to this vehicle)
   * @param {boolean} forceRefresh - Skip cache
   * @returns {Promise<Array>} Array of available devices
   */
  async getAvailableDevices(entityId, vehicleId = null, forceRefresh = false) {
    const cacheKey = `available_${entityId}_${vehicleId || 'none'}`;

    if (!forceRefresh && this.isCacheValid(cacheKey)) {
      console.log('Using cached available GPS devices data');
      return this.cache[cacheKey].data;
    }

    try {
      console.log('Fetching available GPS devices', { entityId, vehicleId });

      // Build URL with optional vehicleId query parameter
      const url = vehicleId
        ? `/api/gps-devices/${entityId}/devices/available?vehicleId=${vehicleId}`
        : `/api/gps-devices/${entityId}/devices/available`;

      const response = await axios.get(url);

      if (response.data.success) {
        this.cache[cacheKey] = {
          data: response.data.data,
          timestamp: Date.now()
        };

        console.log('Available GPS devices fetched successfully', {
          entityId,
          vehicleId,
          count: response.data.data.length
        });
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to fetch available GPS devices');
      }
    } catch (error) {
      console.error('Error fetching available GPS devices', {
        error: error.message,
        entityId,
        vehicleId,
        component: 'GPSDeviceService'
      });

      // Return cached data if available
      if (this.cache[cacheKey]?.data) {
        console.log('Using stale cached available GPS devices due to API error');
        return this.cache[cacheKey].data;
      }

      throw error;
    }
  }

  /**
   * Get GPS device statistics
   * @param {number} entityId - Entity ID
   * @param {boolean} forceRefresh - Skip cache
   * @returns {Promise<Object>} Statistics object
   */
  async getDeviceStats(entityId, forceRefresh = false) {
    const cacheKey = `stats_${entityId}`;

    if (!forceRefresh && this.isCacheValid(cacheKey)) {
      console.log('Using cached GPS device stats');
      return this.cache[cacheKey].data;
    }

    try {
      console.log('Fetching GPS device statistics', { entityId });
      const response = await axios.get(
        `/api/gps-devices/${entityId}/devices/stats`
      );

      if (response.data.success) {
        this.cache[cacheKey] = {
          data: response.data.data,
          timestamp: Date.now()
        };

        console.log('GPS device statistics fetched successfully', {
          entityId,
          stats: response.data.data
        });
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to fetch device statistics');
      }
    } catch (error) {
      console.error('Error fetching GPS device statistics', {
        error: error.message,
        entityId,
        component: 'GPSDeviceService'
      });

      // Return cached data if available
      if (this.cache[cacheKey]?.data) {
        console.log('Using stale cached GPS device stats due to API error');
        return this.cache[cacheKey].data;
      }

      throw error;
    }
  }

  /**
   * Check if device is online
   * @param {Object} device - Device object
   * @param {number} thresholdMinutes - Minutes threshold (default 5)
   * @returns {boolean} Online status
   */
  isDeviceOnline(device, thresholdMinutes = 5) {
    if (!device.last_contact_at) {
      return false;
    }

    const lastContact = new Date(device.last_contact_at);
    const now = new Date();
    const diffMinutes = (now - lastContact) / (1000 * 60);

    return diffMinutes < thresholdMinutes;
  }

  /**
   * Get device health status
   * @param {Object} device - Device object
   * @returns {string} Health status: 'online', 'warning', 'offline'
   */
  getDeviceHealth(device) {
    if (!device.last_contact_at) {
      return 'offline';
    }

    const lastContact = new Date(device.last_contact_at);
    const now = new Date();
    const diffMinutes = (now - lastContact) / (1000 * 60);

    if (diffMinutes < 5) {
      return 'online';
    } else if (diffMinutes < 15) {
      return 'warning';
    } else {
      return 'offline';
    }
  }

  /**
   * Get health icon for device
   * @param {string} health - Health status
   * @returns {string} Icon/emoji
   */
  getHealthIcon(health) {
    switch (health) {
      case 'online':
        return '🟢';
      case 'warning':
        return '🟡';
      case 'offline':
        return '🔴';
      default:
        return '⚪';
    }
  }

  /**
   * Clear cache for specific entity or all
   * @param {number} entityId - Optional entity ID
   */
  clearCache(entityId = null) {
    if (entityId) {
      // Clear specific entity cache
      // Need to clear all keys that start with entity patterns
      const keysToDelete = Object.keys(this.cache).filter(key =>
        key.startsWith(`devices_${entityId}`) ||
        key.startsWith(`stats_${entityId}`) ||
        key.startsWith(`available_${entityId}`)
      );

      keysToDelete.forEach(key => delete this.cache[key]);

      console.log('GPS device cache cleared for entity', {
        entityId,
        keysCleared: keysToDelete.length
      });
    } else {
      // Clear all cache
      this.cache = {};
      console.log('All GPS device cache cleared');
    }
  }
}

// Export singleton instance
export default new GPSDeviceService();
